/**
 * Synchronisation nach AP-7…AP-11 — FM-MEN-08: ISOBUS Event Log.
 *
 * Der Menuepunkt und `ViewKind` existierten seit AP-6, aber `CHAT_SPLIT.md` §4
 * hat das Fenster keinem Arbeitspaket zugewiesen; ein Klick blieb deshalb
 * wirkungslos. Gegenstueck zu `IsobusEventLogWindow` im Original.
 *
 * Datenquelle ist `getSnapshot('isobus-events', cursor)`. Der Cursor kommt aus
 * `nextSeq` der letzten Antwort — der Ringpuffer im Kern vergibt fortlaufende
 * Nummern, selbst wenn er vorne Eintraege verwirft.
 */

import './viewsD.css';
import { createDataTable } from './dataTable';
import { createFloatingPanel } from './filters/FloatingPanel';
import { createButton, createCombo } from '../ui/controls';
import type { CanConnection } from '../app/connection';
import type { AppStore } from '../app/store';

const REFRESH_INTERVAL_MS = 300;
/** `kMaxEvents` des Originals. */
const MAX_ROWS = 5000;

interface IsobusEvent {
  seq: number;
  timestamp: number;
  level: string;
  category: string;
  sa: number;
  text: string;
}

interface EventSnapshot {
  events: IsobusEvent[];
  nextSeq: number;
}

/** 0 = alles, 1 = Warnungen und Fehler, 2 = nur Fehler. */
const LEVEL_FILTERS = [
  { value: '0', label: 'All events' },
  { value: '1', label: 'Warnings + errors' },
  { value: '2', label: 'Errors only' },
];

export interface IsobusEventLogHandle {
  show(): void;
}

function passes(level: string, filter: number): boolean {
  if (filter === 1) return level !== 'INFO';
  if (filter === 2) return level === 'ERROR';
  return true;
}

export function createIsobusEventLog(
  store: AppStore,
  connection: CanConnection,
): IsobusEventLogHandle {
  const panel = createFloatingPanel('ISOBUS Event Log');
  panel.element.classList.add('cm-panel-window--wide');

  let cursor = 0;
  let levelFilter = 0;
  let events: IsobusEvent[] = [];

  const table = createDataTable({
    columns: ['Time', 'Level', 'Cat', 'SA', 'Event'],
    emptyText: 'Noch keine ISOBUS-Ereignisse.',
  });

  const levelCombo = createCombo({
    items: LEVEL_FILTERS,
    value: '0',
    onChange: (value) => {
      levelFilter = Number(value);
      render();
    },
  });

  const clearButton = createButton('Clear', {
    onClick: () => {
      events = [];
      render();
    },
  });

  const toolbar = document.createElement('div');
  toolbar.className = 'cm-view__row';
  const label = document.createElement('span');
  label.textContent = 'Show:';
  toolbar.append(label, levelCombo, clearButton);

  const layout = document.createElement('div');
  layout.className = 'cm-view';
  layout.append(toolbar, table.element);
  panel.body.append(layout);

  function render(): void {
    const rows = events
      .filter((event) => passes(event.level, levelFilter))
      .map((event) => [
        event.timestamp.toFixed(4),
        event.level,
        event.category,
        `0x${event.sa.toString(16).toUpperCase().padStart(2, '0')}`,
        event.text,
      ]);
    table.setRows(rows);
  }

  async function poll(): Promise<void> {
    if (!panel.visible) return;
    try {
      const snapshot = (await connection.getSnapshot('isobus-events', cursor)) as EventSnapshot;
      if (snapshot.events.length > 0) {
        events.push(...snapshot.events);
        if (events.length > MAX_ROWS) events = events.slice(-MAX_ROWS);
        render();
      }
      cursor = snapshot.nextSeq;
    } catch {
      // Ohne geladenen Kern gibt es noch keine Ereignisse; stiller Fehlschlag,
      // der naechste Durchlauf versucht es erneut.
    }
  }

  setInterval(() => void poll(), REFRESH_INTERVAL_MS);

  return {
    show(): void {
      panel.show();
      // ISOBUS-Ereignisse entstehen nur bei aktiver Dekodierung; ohne sie
      // bleibt die Liste leer und der Grund waere nicht erkennbar.
      if (!store.decodeFlags.isobusEnabled) {
        store.emit('status:changed', {
          text: 'ISOBUS Event Log: activate ISOBUS decoding to collect events',
        });
      }
      void poll();
    },
  };
}
